/**
 * Decides which field templates of a form still need a top-level field instance created for them.
 *
 * This is the pure decision behind verifyFormGroupInstances (src/hooks/useForms.tsx). It only ever looks at a form's
 * top-level instances (the ones that carry `formInstance`); table cells are handled separately by verifyTableFields.
 *
 * Two situations are treated as "do not create anything" rather than "everything is missing", because creating in those
 * situations produces a complete duplicate set of fields that hides the real ones:
 *  - none of the loaded instances covers any template ("no-covered-templates"): a form with zero top-level instances is
 *    either a creation that failed part way or a load that came back empty; a template gaining a field never looks like this.
 *  - any loaded instance has no template id at all ("null-template-ids"): top-level instances only lose their template id
 *    when a sync merge corrupted them, and once that has happened nothing about coverage can be trusted.
 */

export interface LoadedFieldInstanceLike {
	fieldTemplateId?: string | null;
	fieldTemplate?: { id?: string | null } | null;
}

export interface FieldTemplateLike {
	id: string;
}

export type MissingFieldInstanceSkipReason = "no-covered-templates" | "null-template-ids";

export interface MissingFieldInstancePlan<T extends FieldTemplateLike> {
	/** The templates that need a new instance, in the order they were supplied */
	templatesToCreate: T[];
	/** Set when creation was refused for the whole form */
	skipReason?: MissingFieldInstanceSkipReason;
	/** How many top-level instances were loaded for the form */
	loadedCount: number;
	/** How many distinct templates those instances cover */
	coveredCount: number;
	/** How many loaded instances carry no template id */
	nullIdCount: number;
}

/**
 * @param loadedFieldInstances The form's top-level field instances as loaded from the database
 * @param templateFieldTemplates The form template's top-level field templates
 * @returns The plan: either a (possibly empty) list of templates to create, or a skip reason
 */
export function planMissingFieldInstances<T extends FieldTemplateLike>(
	loadedFieldInstances: LoadedFieldInstanceLike[],
	templateFieldTemplates: T[],
): MissingFieldInstancePlan<T> {
	const coveredTemplateIds = new Set<string>();
	let nullIdCount = 0;
	for (const fieldInstance of loadedFieldInstances) {
		// Count the template as covered even when the fieldTemplate document failed to resolve (e.g. templates not yet
		// synced to IndexedDB) — the instance exists, so creating another would duplicate it.
		const templateId = fieldInstance.fieldTemplate?.id || fieldInstance.fieldTemplateId;
		if (templateId) {
			coveredTemplateIds.add(templateId);
		} else {
			nullIdCount++;
		}
	}

	const base = {
		loadedCount: loadedFieldInstances.length,
		coveredCount: coveredTemplateIds.size,
		nullIdCount,
	};

	if (nullIdCount > 0) {
		return { ...base, templatesToCreate: [], skipReason: "null-template-ids" };
	}
	if (coveredTemplateIds.size === 0 && templateFieldTemplates.length > 0) {
		return { ...base, templatesToCreate: [], skipReason: "no-covered-templates" };
	}

	const templatesToCreate = templateFieldTemplates.filter((fieldTemplate) => !coveredTemplateIds.has(fieldTemplate.id));
	return { ...base, templatesToCreate };
}

export default planMissingFieldInstances;
